// Account-data export helper.
//
// Wraps `accountApi.exportAccountData()` (which returns a binary ZIP blob
// by default) in the platform-specific "save / share" flow used elsewhere
// in the app. On web we trigger a Blob download; on native we write the
// bytes to the FileSystem cache and hand the file to the OS share sheet.
//
// Mirrors the pattern in `constants/utils/conversationExport.js` so the
// "Download my data" UX feels identical to "Export this conversation".

import { Platform } from 'react-native';
import * as FileSystem from 'expo-file-system';
import * as Sharing from 'expo-sharing';
import { accountApi } from '@/lib/api';

const ZIP_MIME_TYPE = 'application/zip';

function buildFilename() {
  const datestamp = new Date().toISOString().slice(0, 10);
  return `gab-ai-account-data-${datestamp}.zip`;
}

function downloadBlobWeb(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

// Convert a Blob to a base64 string. Used on native because the
// FileSystem API only accepts string content. Reads in one shot — fine
// for a ZIP archive that we already have in memory.
function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Failed to read export archive'));
    reader.onload = () => {
      const result = reader.result || '';
      // `result` is "data:<mime>;base64,<payload>". Strip the prefix so
      // FileSystem.writeAsStringAsync gets pure base64.
      const idx = String(result).indexOf(',');
      resolve(idx >= 0 ? String(result).slice(idx + 1) : String(result));
    };
    reader.readAsDataURL(blob);
  });
}

async function shareFileNative(blob, filename) {
  const fileUri = `${FileSystem.cacheDirectory}${filename}`;
  const base64 = await blobToBase64(blob);
  await FileSystem.writeAsStringAsync(fileUri, base64, {
    encoding: FileSystem.EncodingType.Base64,
  });

  const isAvailable = await Sharing.isAvailableAsync();
  if (!isAvailable) {
    throw new Error('Sharing is not available on this device');
  }

  await Sharing.shareAsync(fileUri, {
    mimeType: ZIP_MIME_TYPE,
    dialogTitle: 'Export account data',
    UTI: 'public.zip-archive',
  });

  // Best-effort cleanup — same window we use in conversationExport.
  setTimeout(async () => {
    try {
      await FileSystem.deleteAsync(fileUri, { idempotent: true });
    } catch (_) {
      // ignore cleanup failures
    }
  }, 60_000);
}

/**
 * Fetch the user's account data export as a ZIP archive and hand it to
 * the platform's native save/share flow. Throws on failure so callers
 * can surface a toast.
 *
 * @returns {Promise<{ filename: string }>} the chosen output filename,
 *   useful for follow-up confirmation toasts.
 */
export async function exportAccountData() {
  const { blob, filename: serverFilename } = await accountApi.exportAccountData({ format: 'zip' });

  // Prefer the server-suggested filename (which carries today's date in
  // the same shape as the rest of the codebase) and fall back to a
  // locally-generated one if a proxy stripped the header.
  const filename = serverFilename || buildFilename();

  if (Platform.OS === 'web') {
    downloadBlobWeb(blob, filename);
  } else {
    await shareFileNative(blob, filename);
  }

  return { filename };
}

export default exportAccountData;
